import { useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'motion/react';

// ── Floating geometric shape ────────────────────────────────────

export function FloatingShape({
  className,
  delay = 0,
  duration = 6,
  rotate = false,
}: {
  className: string;
  delay?: number;
  duration?: number;
  rotate?: boolean;
}) {
  return (
    <motion.div
      className={className}
      animate={{
        y: [0, -14, 0],
        rotate: rotate ? [0, 360] : [0, 0],
      }}
      transition={{
        y: { duration, repeat: Infinity, ease: 'easeInOut', delay },
        rotate: rotate
          ? { duration: duration * 3, repeat: Infinity, ease: 'linear', delay }
          : undefined,
      }}
    />
  );
}

// ── 3D mockup wrapper (browser chrome with tilt) ────────────────

export function MockupWrapper({ children }: { children: React.ReactNode }) {
  const sectionRef = useRef<HTMLDivElement>(null);

  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const rotateY = useSpring(useTransform(rawX, [-0.5, 0.5], [-12, 12]), {
    stiffness: 80,
    damping: 20,
  });
  const rotateX = useSpring(useTransform(rawY, [-0.5, 0.5], [8, -8]), {
    stiffness: 80,
    damping: 20,
  });
  const shadowX = useTransform(rawX, [-0.5, 0.5], [-20, 20]);
  const shadowY = useTransform(rawY, [-0.5, 0.5], [-10, 10]);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!sectionRef.current) return;
    const rect = sectionRef.current.getBoundingClientRect();
    rawX.set((e.clientX - rect.left) / rect.width - 0.5);
    rawY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  const handleMouseLeave = () => {
    rawX.set(0);
    rawY.set(0);
  };

  return (
    <motion.div
      ref={sectionRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7, delay: 0.2 }}
      style={{ perspective: '1200px' }}
    >
      <motion.div style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }} className="relative">
        {/* Glow shadow */}
        <motion.div
          style={{ x: shadowX, y: shadowY }}
          className="absolute -inset-6 bg-gradient-to-br from-blue-500/15 via-violet-500/10 to-cyan-500/10 dark:from-blue-500/10 dark:via-violet-500/5 dark:to-cyan-500/5 rounded-3xl blur-3xl"
        />

        {/* Browser chrome */}
        <div className="relative rounded-2xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
          {/* Title bar */}
          <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
            </div>
            <div className="flex-1 flex justify-center">
              <div className="flex items-center gap-1.5 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md px-3 py-1">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                <span className="text-[11px] text-gray-400 dark:text-gray-500">prixia.app</span>
              </div>
            </div>
          </div>

          {/* Content slot */}
          <div className="p-5 space-y-4 bg-gray-50/50 dark:bg-gray-800/50">{children}</div>
        </div>

        {/* Bottom reflection */}
        <div className="absolute -bottom-8 left-4 right-4 h-8 bg-gradient-to-b from-black/8 to-transparent blur-md rounded-full" />
      </motion.div>
    </motion.div>
  );
}

// ── Section background with gradient orbs + floating shapes ─────

export function ShowcaseBg({ flip = false }: { flip?: boolean }) {
  return (
    <>
      {/* Gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className={`absolute top-1/4 ${flip ? 'right-0' : '-left-32'} w-[400px] h-[400px] bg-blue-400/10 dark:bg-blue-400/5 rounded-full blur-[100px]`}
          animate={{ scale: [1, 1.15, 1], x: flip ? [0, -20, 0] : [0, 20, 0] }}
          transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className={`absolute bottom-1/4 ${flip ? '-left-20' : '-right-20'} w-[350px] h-[350px] bg-violet-400/10 dark:bg-violet-400/5 rounded-full blur-[100px]`}
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 11, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        />
      </div>

      {/* Floating shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <FloatingShape
          delay={0}
          duration={7}
          rotate
          className="absolute top-16 left-[6%] w-10 h-10 border-2 border-blue-300/30 dark:border-blue-400/15 rounded-lg"
        />
        <FloatingShape
          delay={2}
          duration={9}
          rotate
          className="absolute top-24 right-[10%] w-16 h-16 border-2 border-violet-300/25 dark:border-violet-400/15 rounded-full"
        />
        <FloatingShape
          delay={1}
          duration={6}
          className="absolute bottom-20 left-[12%] w-6 h-6 border-2 border-cyan-400/30 dark:border-cyan-400/15 rounded-md rotate-12"
        />
        <FloatingShape
          delay={3}
          duration={8}
          rotate
          className="absolute bottom-16 right-[7%] w-10 h-10 border-2 border-blue-400/20 dark:border-blue-400/10 rounded-sm rotate-45"
        />
      </div>
    </>
  );
}
